import { ref, Ref } from 'vue';
import PricingService from 'services/PricingService';
import OfferingService from 'services/OfferingService';
import CurrencyService from 'services/CurrencyService';
import FlexGroupService from 'services/FlexGroupService';

export interface NormalizedOffering {
  id: number;
  name: string;
  description: string;
  thumbnail: string | null;
  flexgroupid: number;
  inStock: boolean;
  externalid: string;
}

export interface NormalizedCurrency {
  iso: string;
  name: string;
  symbol: string;
}

export interface NormalizedFlexGroup {
  id: number;
  name: string;
}

export interface PriceRecord {
  id: number;
  offeringid: number;
  currency: string;
  price_base: number | null;
  price_base_from: number | null;
  price_page: number | null;
  price_page_from: number | null;
}

export interface PricingDataState {
  prices: Ref<PriceRecord[]>;
  offerings: Ref<NormalizedOffering[]>;
  currencies: Ref<NormalizedCurrency[]>;
  flexGroups: Ref<NormalizedFlexGroup[]>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  loadData: () => Promise<void>;
  getOfferingById: (id: number) => NormalizedOffering | undefined;
  getCurrencyByIso: (iso: string) => NormalizedCurrency | undefined;
}

export function usePricingData(): PricingDataState {
  const prices = ref<PriceRecord[]>([]);
  const offerings = ref<NormalizedOffering[]>([]);
  const currencies = ref<NormalizedCurrency[]>([]);
  const flexGroups = ref<NormalizedFlexGroup[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const normalizeOffering = (raw: any): NormalizedOffering => ({
    id: raw.id,
    name: raw.name || '',
    description: raw.description || '',
    thumbnail: raw.thumbnail || null,
    flexgroupid: raw.flexgroupid ?? raw.flexGroupId ?? raw.flex_group_id ?? 0,
    inStock: raw.instock === 1 || raw.instock === true || raw.inStock === 1 || raw.inStock === true,
    externalid: raw.externalid || '',
  });

  const normalizeCurrency = (raw: any): NormalizedCurrency => ({
    iso: raw.iso || raw.code || '',
    name: raw.name || '',
    symbol: raw.symbol || raw.iso || '',
  });

  const normalizeFlexGroup = (raw: any): NormalizedFlexGroup => ({
    id: raw.id,
    name: raw.name || `Group ${raw.id}`,
  });

  const loadData = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const params = new URLSearchParams({ limit: '0' });
      const [pricesData, offeringsData, currenciesData, flexGroupsData] = await Promise.all([
        PricingService.getAll(params),
        OfferingService.getAll(params),
        CurrencyService.getAll(params),
        FlexGroupService.getAll(params),
      ]);
      
      prices.value = pricesData || [];
      offerings.value = (offeringsData || []).map(normalizeOffering);
      currencies.value = (currenciesData || []).map(normalizeCurrency);
      flexGroups.value = (flexGroupsData || []).map(normalizeFlexGroup);
    } catch (err: any) {
      error.value = err.message || 'Failed to load pricing data';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getOfferingById = (id: number): NormalizedOffering | undefined => {
    return offerings.value.find(o => o.id === id);
  };

  const getCurrencyByIso = (iso: string): NormalizedCurrency | undefined => {
    return currencies.value.find(c => c.iso === iso);
  };

  return {
    prices,
    offerings,
    currencies,
    flexGroups,
    isLoading,
    error,
    loadData,
    getOfferingById,
    getCurrencyByIso,
  };
}
